// Fan chart: the classic ancestor wheel. You at the centre, parents in the
// first ring, grandparents in the second, each generation doubling outward.
//
// The empty arcs are load-bearing, not a rendering accident. A genealogy chart
// draws an unrecorded great-grandparent as nothing at all, so the one thing a
// half-finished tree most needs to tell you - which generation is missing - is
// exactly what it cannot show. Here a blank ring is impossible to miss.
//
// Ancestors only. Siblings, cousins and descendants have no slot in a fan by
// definition; the tree and timeline views cover those.
import { ancestorSlots, birthOf, personOpenable, applyPersonLink } from "./genealogy"

const MAX_RINGS = 4
const CENTER_R = 46
const RING_W = 62
const GAP = 1.4          // degrees trimmed off each arc so segments read separately
const SWEEP = 300        // total degrees of the fan, centred on straight up
const LABEL_MIN_ARC = 22 // px of arc length below which a label won't fit

const polar = (cx, cy, r, deg) => {
    let rad = ((deg - 90) * Math.PI) / 180
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)]
}

// An annular sector: outer arc left-to-right, straight in, inner arc back.
function sectorPath(cx, cy, rInner, rOuter, from, to) {
    let [x1, y1] = polar(cx, cy, rOuter, from)
    let [x2, y2] = polar(cx, cy, rOuter, to)
    let [x3, y3] = polar(cx, cy, rInner, to)
    let [x4, y4] = polar(cx, cy, rInner, from)
    let large = to - from > 180 ? 1 : 0
    return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4} Z`
}

export function renderFan(ctx) {
    let wrap = document.createElement("div")
    wrap.className = "ft-fan"

    // Draw as far out as the data reaches PLUS one empty ring - the invitation
    // to fill it in. Always showing MAX_RINGS instead would surround two filled
    // rings with three empty ones, which reads as a broken chart rather than an
    // unfinished tree.
    let probe = ancestorSlots({ egoId: ctx.egoId, parentsOf: ctx.parentsOf, nodesById: ctx.nodesById, rings: MAX_RINGS })
    let deepest = probe.reduce((max, slot) => (slot.node ? Math.max(max, slot.ring) : max), 0)
    let rings = Math.min(MAX_RINGS, Math.max(2, deepest + 1))
    let slots = probe.filter((slot) => slot.ring <= rings)
    if (!ctx.egoId) {
        // Three different reasons for the same missing centre, and only one of
        // them is a tree to go and fix.
        //
        // The filter case first, because it is the one that reads as a bug: you
        // ARE on this tree, you can see yourself on it in the table, and the fan
        // says you do not exist. Sending that person off to link themselves to
        // a parent would be sending them to repair something that is already
        // right.
        //
        // Then: the owner can fix an actually-centreless tree; a visitor on a
        // shared link cannot, and telling them to "link yourself" is addressing
        // the wrong person entirely - a share link addressed to somebody in the
        // tree DOES have a centre, so the one that lands here is anonymous.
        let message
        if (ctx.egoFilteredOut) {
            message = "A fan chart is drawn outward from you, and the filter you've picked leaves you out of the tree. Show everyone, or narrow a different way, and the wheel comes back."
        } else if (ctx.publicView) {
            message = "A fan chart is drawn outward from one person, and this shared tree doesn't say who that should be. The other layouts show the whole family."
        } else {
            message = "A fan chart is drawn outward from you, and there's no “you” on this tree yet. Link yourself to a parent or a partner and the wheel will have a centre to grow from."
        }
        wrap.appendChild(emptyState(message))
        return { element: wrap, panZoom: false }
    }
    if (!slots.some((slot) => slot.node)) {
        wrap.appendChild(emptyState("No ancestors recorded yet. Add your parents from a card's ⋮ menu and they'll fill the first ring."))
        return { element: wrap, panZoom: false }
    }

    let outerR = CENTER_R + rings * RING_W
    let size = 2 * (outerR + 8)
    let cx = size / 2
    let cy = size / 2

    let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("viewBox", `0 0 ${size} ${size}`)
    svg.setAttribute("width", String(size))
    svg.setAttribute("height", String(size))
    svg.classList.add("ft-fan-svg")

    let start = -SWEEP / 2
    slots.forEach((slot) => {
        let arc = SWEEP / slot.total
        let from = start + slot.index * arc + GAP / 2
        let to = start + (slot.index + 1) * arc - GAP / 2
        let rInner = CENTER_R + (slot.ring - 1) * RING_W
        let rOuter = rInner + RING_W

        let group = document.createElementNS("http://www.w3.org/2000/svg", "g")
        group.classList.add("ft-fan-slot")
        if (!slot.node) group.classList.add("ft-fan-slot--empty")

        let path = document.createElementNS("http://www.w3.org/2000/svg", "path")
        path.setAttribute("d", sectorPath(cx, cy, rInner, rOuter, from, to))
        path.classList.add("ft-fan-seg")
        group.appendChild(path)

        if (slot.node) {
            group.dataset.nodeId = slot.node.id
            let birth = birthOf(slot.node)
            let when = birth.known ? ` · ${birth.year}` : ""
            let title = document.createElementNS("http://www.w3.org/2000/svg", "title")
            title.textContent = `${slot.node.name}${when}${slot.node.relation ? ` · ${slot.node.relation}` : ""}`
            group.appendChild(title)

            let mid = (from + to) / 2
            let rMid = (rInner + rOuter) / 2
            let arcPx = ((to - from) * Math.PI / 180) * rMid
            if (arcPx >= LABEL_MIN_ARC) {
                group.appendChild(label(slot.node, cx, cy, rMid, mid, arcPx))
            }
            if (personOpenable(slot.node)) {
                let link = document.createElementNS("http://www.w3.org/2000/svg", "a")
                applyPersonLink(link, slot.node)
                link.appendChild(group)
                svg.appendChild(link)
                return
            }
        }
        svg.appendChild(group)
    })

    // The hub: you.
    let hub = document.createElementNS("http://www.w3.org/2000/svg", "circle")
    hub.setAttribute("cx", String(cx))
    hub.setAttribute("cy", String(cy))
    hub.setAttribute("r", String(CENTER_R - 2))
    hub.classList.add("ft-fan-hub")
    svg.appendChild(hub)

    let me = ctx.nodesById.get(ctx.egoId)
    let hubText = document.createElementNS("http://www.w3.org/2000/svg", "text")
    hubText.setAttribute("x", String(cx))
    hubText.setAttribute("y", String(cy + 4))
    hubText.setAttribute("text-anchor", "middle")
    hubText.classList.add("ft-fan-hub-label")
    hubText.textContent = firstName(me?.name) || "You"
    svg.appendChild(hubText)

    wrap.appendChild(svg)
    wrap.appendChild(legend(slots))
    return { element: wrap, panZoom: true, width: size, height: size }
}

// Two things make a fan label readable, and the first version got both wrong.
//
// Orientation: an inner ring has huge arcs and a fixed ring width, so its text
// has far more room ALONG the arc than across it - the reverse is true once the
// slots get narrow. So pick whichever axis is roomier per slot, instead of
// always running text radially (which capped every name at ~9 characters).
//
// Shortening: chop by name part, not mid-word. "Marleen Van de Velde" becomes
// "Marleen V." and then "Marleen", never "Marleen V…". The full name and dates
// are on the segment's tooltip either way.
function label(node, cx, cy, r, deg, arcPx) {
    let [x, y] = polar(cx, cy, r, deg)
    let rotation
    let room
    if (arcPx > RING_W * 2.2) {
        // A wedge much wider along its arc than it is deep - the inner rings,
        // where there are only two or four slots. Text set along the arc here
        // comes out on a steep diagonal, because a straight label can only
        // approximate a 150-degree curve. Horizontal is simply easier to read,
        // and a wedge this size has room for it in every direction.
        rotation = 0
        room = RING_W * 1.5
    } else if (arcPx > RING_W * 1.15) {
        // Roomier along the arc than across the ring: set it tangentially,
        // flipped on the lower half so nothing reads upside down.
        rotation = Math.abs(deg) > 90 ? deg + 180 : deg
        // A straight label centred on a curved band drifts outside it near the
        // ends, so only part of the arc is usable.
        room = arcPx * 0.62
    } else {
        // Narrow outer slots: radial is the only axis with any room left.
        rotation = deg > 0 ? deg - 90 : deg + 90
        room = RING_W - 12
    }

    let text = document.createElementNS("http://www.w3.org/2000/svg", "text")
    text.setAttribute("x", String(x))
    text.setAttribute("y", String(y))
    text.setAttribute("text-anchor", "middle")
    text.setAttribute("dominant-baseline", "middle")
    text.setAttribute("transform", `rotate(${rotation} ${x} ${y})`)
    text.classList.add("ft-fan-label")
    text.textContent = fitName(node.name, room)
    return text
}

const CHAR_PX = 6.4

// Longest form of the name that fits: full name, then first name plus the
// surname's initial, then the first name alone, then initials.
//
// Deliberately NOT "first word + last word": this family is full of Dutch
// tussenvoegsels, and dropping them turns "Marleen Van de Velde" into "Marleen
// Velde" - a name that does not exist and reads as a data error rather than an
// abbreviation. Everything after the first word is one surname phrase, and it
// is either kept whole or reduced to its initial.
function fitName(name, room) {
    let parts = String(name || "").trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return ""
    let first = parts[0]
    let surname = parts.slice(1).join(" ")
    let candidates = [name]
    if (surname) candidates.push(`${first} ${surname[0]}.`)
    candidates.push(first)
    if (surname) candidates.push(`${first[0]}${surname[0]}`)
    return candidates.find((candidate) => candidate.length * CHAR_PX <= room) || candidates[candidates.length - 1]
}

function firstName(name) {
    return String(name || "").trim().split(/\s+/)[0] || ""
}

// How complete each generation is - the number a half-built tree actually
// wants, and the reason to bother scrolling out to an empty ring.
function legend(slots) {
    let box = document.createElement("div")
    box.className = "ft-fan-legend"
    let byRing = new Map()
    slots.forEach((slot) => {
        let entry = byRing.get(slot.ring) || { filled: 0, total: 0 }
        entry.total += 1
        if (slot.node) entry.filled += 1
        byRing.set(slot.ring, entry)
    })
    const NAMES = { 1: "Parents", 2: "Grandparents", 3: "Great-grandparents", 4: "2× great-grandparents" }
    // Only the drawn rings appear here, so the legend can't advertise a
    // generation the wheel isn't showing.
    ;[...byRing.keys()].sort((a, b) => a - b).forEach((ring) => {
        let { filled, total } = byRing.get(ring)
        let item = document.createElement("span")
        item.className = `ft-fan-legend-item${filled === 0 ? " is-empty" : ""}`
        item.textContent = `${NAMES[ring] || `Ring ${ring}`} ${filled}/${total}`
        box.appendChild(item)
    })
    return box
}

function emptyState(message) {
    let box = document.createElement("div")
    box.className = "ft-view-empty"
    let h = document.createElement("h2")
    h.className = "ft-view-empty-title"
    h.textContent = "Nothing to fan out yet"
    box.appendChild(h)
    let p = document.createElement("p")
    p.className = "text-muted small mb-0"
    p.textContent = message
    box.appendChild(p)
    return box
}
